import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import env from "app/utils/env";
import type { HotelAccommodationDescription } from "app/features/hotel/hotelDescription/types";
import { mapHotelOnlyAccommodation } from "app/features/hotel/hotelDescription/utils/mapHotelOnlyAccommodation";
import type { Tunnel } from "app/types/tunnel";

const fetchHotelAccommodationDescription = async (
	accommodationId: string,
	tunnel: Tunnel,
	partnerCode: string,
	bookingFlowId?: string | null
): Promise<HotelAccommodationDescription> => {
	const isHotelOnly = tunnel === "hotelonly";
	const path = isHotelOnly
		? `/hotelonly/accommodations/${accommodationId}`
		: `/${tunnel}/accommodationDescription/${accommodationId}`;

	const response = await axios.get(`${env("CACHED_API_URL")}${path}`, {
		params: { partnerCode },
		...(bookingFlowId && { headers: { "Booking-Flow-Id": bookingFlowId } }),
	});

	return isHotelOnly
		? mapHotelOnlyAccommodation(response.data)
		: (response.data as HotelAccommodationDescription);
};

export const getHotelAccommodationDescriptionQuery = (
	accommodationId: string | undefined,
	tunnel: Tunnel,
	partnerCode: string,
	bookingFlowId?: string | null
) => ({
	queryKey: [
		"hotelAccommodationDescription",
		accommodationId,
		tunnel,
		partnerCode,
		bookingFlowId,
	],
	queryFn: () =>
		fetchHotelAccommodationDescription(
			accommodationId as string,
			tunnel,
			partnerCode,
			bookingFlowId
		),
	enabled: Boolean(accommodationId) && Boolean(partnerCode),
	staleTime: 1000 * 60 * 5,
});

export const useHotelAccommodationDescription = (
	accommodationId: string | undefined,
	tunnel: Tunnel,
	partnerCode: string,
	bookingFlowId?: string | null
) =>
	useQuery(
		getHotelAccommodationDescriptionQuery(accommodationId, tunnel, partnerCode, bookingFlowId)
	);
